// Libs
import { useEffect, useMemo, useState, Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { HelmetProvider, Helmet } from 'react-helmet-async'
import useSound from 'use-sound'

import getWindowSize from './utils/hooks/getWindowSize'

// Utils & Data
import {
  setActiveUser,
  setCardHand,
  getCardHand,
  readDefeatedAntagonists,
  setDefeatedAntagonists,
  readTokens,
  setTokens,
  readWrongAnswers,
  resetWrongAnswers,
  setFirstTimePlaying,
  getFirstTimePlaying,
  setFirstTimeLootBox,
  getFirstTimeLootBox,
  setAvatar,
  setAvatarPartCollection,
  getAvatar,
  readSettings,
} from './api/storage'
import { getDefaultAvatorParts, getRandomAvatar } from './api/engine'

import OptionsContext from './utils/OptionsContext'
import StatsContext from './utils/StatsContext'
import { STAT_FLAGS } from './utils/constants'

// Resources
import titleTrack from './assets/sounds/fx/00-title.mp3'
import styles from './App.module.scss'
import { Button, ConfigIcon, Modal, Settings, Loader } from './components'
import useAnimation from './utils/hooks/useAnimation'
import avatarData from './data/avatar.json'
import CookiePolicy from './views/CookiePolicy/CookiePolicy'
import MobileCollectionViewer from './views/CollectionViewer/MobileCollectionViewer'
// import useCheckForApp from './utils/useCheckForApp'

const Help = lazy(() => import('./views/Help/Help'))
const Home = lazy(() => import('./views/Home/Home'))
const DeckBuilder = lazy(() => import('./views/DeckBuilder/DeckBuilder'))
const Guidance = lazy(() => import('./views/Guidance/Guidance'))
const Start = lazy(() => import('./views/Start/Start'))
const Scenarios = lazy(() => import('./views/Scenarios/Scenarios'))
const AvatarBuilder = lazy(() => import('./views/AvatarBuilder/AvatarBuilder'))
const Persuade = lazy(() => import('./views/Persuade/Persuade'))
const LootBox = lazy(() => import('./views/LootBox/LootBox'))
const CollectionViewer = lazy(() =>
  import('./views/CollectionViewer/CollectionViewer')
)
const Manual = lazy(() => import('./views/Manual/Manual'))
const Stats = lazy(() => import('./views/Stats/Stats'))

const App = () => {
  const { height, width } = getWindowSize()
  const isMobile = width < 768
  const [showModal, setShowModal] = useState(false)
  const [dataInitialized, setDataInitialized] = useState(false)
  const [currentVoiceover, setCurrentVoiceover] = useState()
  const [shouldShowAlertBox, setShouldShowAlertBox] = useState(true)
  const { t, i18n } = useTranslation()
  const [options, setOptions] = useState({
    shouldReduceMotion: false,
    highContrast: false,
    themeVolume: 0.5,
    effectsVolume: 0.5,
    themeMusicOn: false,
    soundEffectsOn: false,
    voiceover: true,
    language: 'sv',
  })

  const [statFlags, setStatFlags] = useState({
    [STAT_FLAGS.IS_FIRST_TIME_START]: true,
    [STAT_FLAGS.IS_FIRST_TIME_HOME]: true,
    [STAT_FLAGS.IS_FIRST_TIME_WIN]: true,
    [STAT_FLAGS.IS_FIRST_TIME_WIN_THREE]: true,
    [STAT_FLAGS.IS_FIRST_TIME_WIN_TEN]: true,
    [STAT_FLAGS.IS_FIRST_TIME_WIN_GAME_COMPLETE]: true,
    [STAT_FLAGS.IS_FIRST_TIME_COLLECTION_VIEWER]: true,
    [STAT_FLAGS.IS_FIRST_TIME_COLLECTION_VIEW_APP]: true,
    [STAT_FLAGS.IS_FIRST_TIME_PERSUATION]: true,
  })

  const [isApp, setApp] = useState(false)
  //*a custom hook checking if it is an app or not.
  // const checkingWithStandalone = useCheckForApp()
  const appSite = window.location.href.endsWith('collection-viewer/app')

  useEffect(() => {
    if (appSite) {
      setApp(true)
      setOptions((prevState) => ({ ...prevState, shouldReduceMotion: false }))
    }
  }, [appSite])

  const statsContextValue = useMemo(() => {
    return { statFlags, setStatFlags }
  }, [statFlags])

  const [, { sound }] = useSound(titleTrack, {
    loop: true,
    volume: 0,
    interrupt: true,
    autoplay: true,
  })
  const reducedMotion = useReducedMotion() || isMobile
  const [animate, trigger] = useAnimation({
    rotation: options.shouldReduceMotion ? 0 : 40,
  })

  const contextValue = useMemo(() => {
    return { options, setOptions }
  }, [options])

  async function hasVoiceover(path) {
    try {
      const imported = (
        await import(
          './assets/sounds/voiceover/' + options.language + '/' + path + '.mp3'
        )
      ).default
      return imported
    } catch (error) {
      return false
    }
  }

  async function playVoiceover(path) {
    try {
      const imported = (
        await import(
          './assets/sounds/voiceover/' + options.language + '/' + path + '.mp3'
        )
      ).default
      const audio = new Audio(imported)
      audio.volume = options.effectsVolume || 0.5
      // pause currently playing voiceover, replace with new audio
      currentVoiceover?.pause()
      setCurrentVoiceover(audio)
      audio.play()
      return audio
    } catch (error) {
      console.log(error)
    }
  }

  async function playSoundEffect(playSound, delay = 0) {
    setTimeout(() => {
      options.soundEffectsOn && playSound()
    }, delay)
  }

  function toggleThemeSound(soundOn = options.themeMusicOn) {
    const currentVolume = sound?._volume
    // if sound on and not playing / muted
    if (sound && soundOn && currentVolume !== options.themeVolume) {
      sound.fade(currentVolume || 0, options.themeVolume, 400)
      // if sound off and not muted
    } else if (sound && !soundOn && currentVolume > 0) {
      sound.fade(currentVolume || 0, 0, 400)
    }
  }

  const init = async () => {
    // TODO: Set a user in a login state
    setActiveUser({ name: 'JohnDoe' })

    const [
      avatar,
      firstTimePlaying,
      firstTimeLootBox,
      cards,
      defeatedAntagonists,
      tokens,
      wrongAnswers,
    ] = await Promise.all([
      getAvatar(),
      getFirstTimePlaying(),
      getFirstTimeLootBox(),
      getCardHand(),
      readDefeatedAntagonists(),
      readTokens(),
      readWrongAnswers(),
    ])

    if (!avatar) {
      const defaultAvatarParts = getDefaultAvatorParts(avatarData.parts)
      const randomAvatar = getRandomAvatar(
        defaultAvatarParts,
        avatarData.colors
      )
      await Promise.all([
        setAvatarPartCollection(defaultAvatarParts),
        setAvatar(randomAvatar),
      ])
    }

    if (firstTimePlaying == null) {
      await setFirstTimePlaying(true)
    }

    if (firstTimeLootBox == null) {
      await setFirstTimeLootBox(true)
    }

    if (!cards) {
      await setCardHand([])
    }

    if (!defeatedAntagonists) {
      await setDefeatedAntagonists([])
    }

    if (!tokens) {
      await setTokens(0)
    }

    if (!wrongAnswers) {
      await resetWrongAnswers()
    }

    setDataInitialized(true)
  }

  useEffect(() => {
    ;(async function () {
      await init()
      const settings = await readSettings(reducedMotion)
      setOptions(settings)
    })()
  }, [reducedMotion])

  useEffect(() => {
    i18n.changeLanguage(options.language)
  }, [options.language, i18n])

  if (!dataInitialized) {
    return <Loader />
  }

  function handleModal() {
    setShowModal(!showModal)
    document.querySelector('html').classList.toggle('scroll-lock')
  }

  return (
    <HelmetProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Helmet>
            {/* TODO: Change to correct content */}
            <title>{t('common:meta:title:start')}</title>
            <meta
              name='description'
              content={t('common:meta:description:start')}
            />

            {/* OpenGraph tags */}
            <meta
              property='og:title'
              content='Barnrättspelet - My Human Rights'
            />
            <meta property='og:url' content='https://barnrattsspelet.se/' />
            <meta property='og:locale' content='sv_SE' />
            <meta property='og:image' content='' />
            <meta property='og:image:alt' content='' />
            <meta
              property='og:description'
              content={t('common:meta:description:start')}
            />
            <meta property='og:site_name' content='Barnrättsspelet' />
            <meta property='og:type' content='website' />
            <meta property='og:image:width' content='1200' />
            <meta property='og:image:height' content='630' />
            <meta property='og:image:alt' content='My Human Rights' />
            <meta property='og:image:type' content='image/jpeg' />

            {/* Twitter tags */}
            <meta name='twitter:card' content='summary_large_image' />
            <meta
              name='twitter:title'
              content='My human rights - Alla människor är lika mycket värda.'
            />
            <meta
              name='twitter:description'
              content='My human rights - Alla människor är lika mycket värda. Det är fundamentet som mänskliga rättigheter bygger på. Därför finns My Human Rights'
            />
            <meta
              name='twitter:image'
              content='https://myhumanrights.se/wp-content/uploads/2019/06/facebook.jpg'
            />
          </Helmet>
          <StatsContext.Provider value={{ ...statsContextValue }}>
            <OptionsContext.Provider
              value={{
                ...contextValue,
                toggleThemeSound,
                hasVoiceover,
                playVoiceover,
                playSoundEffect,
                isMobile,
                shouldShowAlertBox,
                setShouldShowAlertBox,
              }}
            >
              <div
                className={`${styles.App} ${
                  /* options.language === 'en' ||  */ options.language ===
                    'ar' && 'right-to-left'
                }`}
                style={{ height: height }}
              >
                <div className={styles.wrapper}>
                  <div className={styles.configWrapper}>
                    <Button
                      variant='secondary'
                      size='small'
                      onClick={handleModal}
                      onMouseEnter={trigger}
                      aria-label={t('common:settings:open')}
                    >
                      <motion.span className={styles.config} animate={animate}>
                        <ConfigIcon />
                      </motion.span>
                    </Button>
                  </div>
                  <AnimatePresence>
                    {showModal && (
                      <Modal onModalClose={handleModal}>
                        <Settings {...{ handleModal, isApp }} />
                      </Modal>
                    )}
                  </AnimatePresence>

                  <Routes>
                    <Route exact path='/' element={<Start />} />
                    <Route path='/help' element={<Help />} />
                    <Route path='/home' element={<Home />} />
                    <Route path='/deck-builder' element={<DeckBuilder />} />
                    <Route path='/guidance' element={<Guidance />} />
                    <Route path='/manual' element={<Manual />} />
                    <Route path='/cookie-policy' element={<CookiePolicy />} />
                    <Route
                      path='/collection-viewer'
                      element={<CollectionViewer />}
                    />
                    <Route
                      path='/collection-viewer/app'
                      element={<MobileCollectionViewer />}
                    />
                    <Route path='/scenarios' element={<Scenarios />} />
                    <Route path='/avatar-builder' element={<AvatarBuilder />} />
                    <Route path='/persuade' element={<Persuade />} />
                    <Route path='/loot-box' element={<LootBox />} />
                    <Route path='/stats' element={<Stats />} />
                  </Routes>
                </div>
              </div>
            </OptionsContext.Provider>
          </StatsContext.Provider>
        </Suspense>
      </BrowserRouter>
    </HelmetProvider>
  )
}

export default App
